import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaCubes,
    FaGlobeAmericas,
    FaShieldAlt,
    FaSignature
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp,
    TiChartLineOutline,
    TiChevronRight,
    TiFlash,
    TiLightbulb,
    TiRadarOutline,
    TiWorldOutline
} from "react-icons/ti";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Partners = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.05,
            scrollTrigger: {
              trigger: section,
              start: "top 90%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const partners = [
    {
      name: "Machu Design",
      role: "Architectural Technologist",
      desc: "Innovative architecture transforming spaces with precision.",
      url: "https://machudesign.com",
      logo: "/images/partner-logos/machudesign.webp",
      tag: "Architectural Sovereignty"
    },
    {
      name: "Modish0161",
      role: "Senior Software Developer",
      desc: "Crafting the future of software and technical infrastructure.",
      url: "https://modish0161.com",
      logo: "/images/partner-logos/Modish0161.png",
      tag: "Technical Excellence"
    },
    {
      name: "Moo-Lah.io",
      role: "Editor-In-Chief",
      desc: "Financial intelligence and wealth growth acceleration.",
      url: "https://moo-lah.io",
      logo: "/images/partner-logos/moolah-icon.png",
      tag: "Financial Intelligence"
    },
    {
      name: "ZeroArtNFT",
      role: "Art Direction & Curation",
      desc: "Bridging fine art with conservation digital assets.",
      url: "https://zeroartnft.com",
      logo: "/images/partner-logos/zeroartlogo.png",
      tag: "Creative Impact"
    },
    {
      name: "Zero-Blockchain",
      role: "Blockchain Engineer",
      desc: "The core consensus and security layer of the ecosystem.",
      url: "https://zero-blockchain.xyz",
      logo: "/images/partner-logos/Zero-Blockchain-Logo.png",
      tag: "Consensus Layer"
    },
    {
      name: "Zero-Bank",
      role: "Chief Executive Officer",
      desc: "Pioneering institutional-grade digital banking solutions.",
      url: "https://zero-bank.io",
      logo: "/images/partner-logos/zero-bank-logo.png",
      tag: "Executive FinTech"
    },
    {
      name: "Zero-Art",
      role: "Senior Art Curator",
      desc: "Institutional curation of world-class digital heritage.",
      url: "https://zero-art.com",
      logo: "/images/partner-logos/zeroartlogo.png", // Reusing ZeroArt logo as it fits curation
      tag: "Cultural Provenance"
    },
    {
      name: "Cannabeedee",
      role: "Chief Executive Officer",
      desc: "Leading-edge executive hub for sustainable growth.",
      url: "https://cannabeedee.com",
      logo: "/images/partner-logos/CannaBeeDee.png",
      tag: "Executive Strategy"
    },
    {
      name: "ReguSync",
      role: "Chief Technical Officer",
      desc: "Harmonizing global regulations through technical synchronization.",
      url: "https://ReguSync.com",
      logo: "/images/partner-logos/ReguSync_favicon.png",
      tag: "Regulatory Intelligence"
    },
    {
      name: "Design-Zero",
      role: "Chief Executive Officer",
      desc: "Defining the aesthetic frontier of the Zero-Ecosystem.",
      url: "https://design-zero.com",
      logo: "/images/partner-logos/Design-Zero-Favicon.png",
      tag: "Executive Design"
    },
    {
      name: "No-Brainer.io",
      role: "Chief Technical Officer",
      desc: "Simplifying complex technical intelligence for global reach.",
      url: "https://No-Brainer.io",
      logo: "/images/partner-logos/No-Brainer-Logo.png",
      tag: "Technical Logic"
    },
    {
      name: "Fior Ribelle",
      role: "Chief Executive Officer",
      desc: "The executive lifestyle and premium brand foundation.",
      url: "#",
      logo: "/images/partner-logos/Fior Ribelle Favicon Logo.png",
      tag: "Executive Lifestyle"
    }
  ];

  const synergyPillars = [
    { title: "Infrastructure", desc: "Modish0161 & No-Brainer provide the raw technical power.", icon: <TiRadarOutline /> },
    { title: "Capital", desc: "Moo-Lah & Zero-Bank drive the financial pulse of the alliance.", icon: <TiChartLineOutline /> },
    { title: "Design", desc: "Machu Design & Design-Zero define the structural aesthetics.", icon: <TiLightbulb /> },
    { title: "Impact", desc: "Zero-Art & Zero-Blockchain ensure planetary conservation.", icon: <TiWorldOutline /> }
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50 font-sans">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/partners-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="partnersHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={40}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="The Power of Alliance"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Forging the Future with 12 Strategic Visionaries
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Partners Bento Grid */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-48">
          {partners.map((partner, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              ref={(el) => (sectionRefs.current[index] = el)}
              className="group relative p-8 bg-white/5 border border-white/10 rounded-[2.5rem] hover:border-blue-500/40 transition-all cursor-pointer overflow-hidden h-[340px] flex flex-col"
              onClick={() => partner.url !== "#" && window.open(partner.url, '_blank')}
            >
              <div className="absolute top-0 right-0 p-8 text-white/5 group-hover:text-blue-500/10 transition-colors pointer-events-none">
                <TiRadarOutline size={120} />
              </div>
              
              <div className="w-32 h-32 rounded-[2rem] bg-white/5 border border-white/10 flex items-center justify-center mb-8 group-hover:scale-105 transition-transform overflow-hidden group-hover:bg-white/10 shadow-inner">
                {partner.logo ? (
                  <img 
                    src={partner.logo} 
                    alt={partner.name} 
                    className="w-full h-full object-contain p-2 transition-all duration-500 group-hover:drop-shadow-[0_0_20px_rgba(59,130,246,0.5)]" 
                  />
                ) : (
                  <div className="text-5xl">{partner.icon}</div>
                )}
              </div>

              <div className="flex-grow">
                <div className="flex items-center gap-2 text-[8px] font-bold text-blue-400 uppercase tracking-widest mb-3">
                   <span className="w-4 h-px bg-blue-400"></span> {partner.tag}
                </div>
                <h3 className="text-xl font-bold text-white mb-1">{partner.name}</h3>
                <div className="text-[10px] font-medium text-gray-500 mb-4 uppercase tracking-[0.1em]">{partner.role}</div>
                <p className="text-gray-300 text-xs leading-relaxed line-clamp-3">
                  {partner.desc}
                </p>
              </div>

              <div className="mt-6 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-[9px] font-bold text-blue-400 uppercase tracking-widest">Connect Hub</span>
                <TiChevronRight className="text-blue-400 text-xl" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Global Synergy Pillars */}
        <div className="mb-48" ref={(el) => (sectionRefs.current[12] = el)}>
          <div className="text-center mb-24">
             <div className="flex items-center justify-center gap-3 text-emerald-400 font-bold uppercase tracking-[0.4em] text-[10px] mb-6">
                <span className="w-12 h-px bg-emerald-400"></span>
                Ecosystem Integration
                <span className="w-12 h-px bg-emerald-400"></span>
             </div>
             <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                Institutional <span className="text-emerald-400 font-zentry italic uppercase tracking-tighter">Synergy</span>
             </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
             {synergyPillars.map((pillar, index) => (
                <div key={index} className="p-10 bg-white/5 border border-white/10 rounded-[3rem] hover:bg-white/10 transition-all text-center group">
                   <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-4xl text-emerald-400 mx-auto mb-8 group-hover:scale-110 transition-transform">
                      {pillar.icon}
                   </div>
                   <h4 className="text-xl font-bold text-white mb-4 uppercase">{pillar.title}</h4>
                   <p className="text-gray-300 text-sm leading-relaxed">{pillar.desc}</p>
                </div>
             ))}
          </div>
        </div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative rounded-[4rem] overflow-hidden border border-white/10 p-1"
          ref={(el) => (sectionRefs.current[13] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-900/40 to-cyan-500/20" />
          <div className="absolute inset-0 bg-black/80 backdrop-blur-3xl" />
          
          <div className="relative z-10 py-32 px-8 text-center max-w-4xl mx-auto">
            <div className="w-24 h-24 rounded-[2.5rem] bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-5xl text-blue-400 mx-auto mb-12 shadow-[0_0_50px_rgba(59,130,246,0.2)]">
              <FaGlobeAmericas />
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-tight">
              Join the <span className="font-zentry italic text-blue-400 text-6xl uppercase tracking-wider block mt-2">Global Alliance</span>
            </h3>
            <p className="text-gray-300 mb-16 text-lg leading-relaxed max-w-2xl mx-auto">
              We are expanding the alliance. Become an institutional partner and 
              leverage the Zero-Blockchain technical and financial sovereignty.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <GlassButton
                className="px-12 py-6"
                variant="blue"
                onClick={() => {}}
              >
                <div className="flex items-center justify-center gap-3 text-lg">
                  Become a Partner <TiArrowRight className="text-2xl" />
                </div>
              </GlassButton>
              
              <GlassButton
                className="px-12 py-6"
                variant="gray"
                onClick={() => {}}
              >
                <div className="flex items-center justify-center gap-3 text-lg">
                  View Whitepaper <TiFlash className="text-2xl text-blue-400" />
                </div>
              </GlassButton>
            </div>

            <div className="mt-24 flex flex-wrap justify-center gap-12 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
               <div className="flex items-center gap-2 font-bold tracking-widest text-[9px]"><FaShieldAlt /> VERIFIED ALLY</div>
               <div className="flex items-center gap-2 font-bold tracking-widest text-[9px]"><FaCubes /> TECHNOLOGY NODE</div>
               <div className="flex items-center gap-2 font-bold tracking-widest text-[9px]"><FaSignature /> IMPACT CERTIFIED</div>
            </div>
          </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-cyan-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Partners;
